import { UnitedStatesLandingPage } from "./components/UnitedStatesLandingPage";
import { dbk_UnitedStatesServicesCatalog as UnitedStatesServicesCatalog } from "./components/UnitedStatesServicesCatalog";
import { dbkUsPublicCopy } from "./content/markets/usPublic";
import { dbkBuildUsHomepageMetadata } from "../server/markets/usMetadata";
import { MarketIntakePage, UsMarketOnboardingPage } from "./components/MarketIntakePage";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import "./styles/quickbooks.css";

import { getBrowserRoute } from "./app/router";
import { dbk_buildRuntimeUsServicesMetadata } from "./app/usServicesSeo";
import {
  applySeoMetadata,
  buildRuntimeArticleSeoMetadata,
  buildRuntimeCrmSeoMetadata,
  buildRuntimeFortnoxPublicSeoMetadata,
  buildRuntimeQuickBooksSeoMetadata,
  buildRuntimeSeoMetadata,
  resolveSeoKeyFromHash
} from "./app/seo";
import { LandingPage } from "./components/LandingPage";
import {
  getDynamicBooksPublicRatingsBootstrap,
  getDynamicBooksRatingsTarget
} from "./lib/publicRatings";

const AccountSession = lazy(() =>
  import("./components/AccountSession").then(({ AccountSession }) => ({ default: AccountSession }))
);
const ArticlePage = lazy(() =>
  import("./components/ArticlePage").then(({ ArticlePage }) => ({ default: ArticlePage }))
);
const CaseStatusPage = lazy(() =>
  import("./components/CaseStatusPage").then(({ CaseStatusPage }) => ({ default: CaseStatusPage }))
);
const CrmRouteShell = lazy(() =>
  import("./components/CrmRouteShell").then(({ CrmRouteShell }) => ({ default: CrmRouteShell }))
);
const CustomerOnboardingPage = lazy(() =>
  import("./components/CustomerOnboardingPage")
    .then(({ CustomerOnboardingPage }) => ({ default: CustomerOnboardingPage }))
);
const FortnoxActivationUnavailablePage = lazy(() =>
  import("./components/FortnoxActivationUnavailablePage")
    .then(({ FortnoxActivationUnavailablePage }) => ({ default: FortnoxActivationUnavailablePage }))
);
const FortnoxPublicPageLayout = lazy(() =>
  import("./components/FortnoxPublicPageLayout")
    .then(({ FortnoxPublicPageLayout }) => ({ default: FortnoxPublicPageLayout }))
);
const NotFoundPage = lazy(() =>
  import("./components/NotFoundPage").then(({ NotFoundPage }) => ({ default: NotFoundPage }))
);
const QuickBooksPublicPage = lazy(() =>
  import("./components/QuickBooksPublicPage").then(({ QuickBooksPublicPage }) => ({ default: QuickBooksPublicPage }))
);
const QuickBooksActionPage = lazy(() =>
  import("./components/QuickBooksActionPage").then(({ QuickBooksActionPage }) => ({ default: QuickBooksActionPage }))
);
const QuickBooksLegalPage = lazy(() =>
  import("./components/QuickBooksLegalPage").then(({ QuickBooksLegalPage }) => ({ default: QuickBooksLegalPage }))
);

export function App() {
  const locationSnapshot = useBrowserLocationSnapshot();
  const route = getBrowserRoute();
  const seoKey = typeof window === "undefined" ? "home" : resolveSeoKeyFromHash(window.location.hash);
  const articleKey = route.kind === "article" ? route.article.key : null;
  const fortnoxPublicPageKey = route.kind === "fortnox_public" ? route.pageKey : null;

  useEffect(() => {
    if (route.kind === "article") {
      applySeoMetadata(document, buildRuntimeArticleSeoMetadata(route.article));
      return;
    }

    if (route.kind === "crm") {
      applySeoMetadata(document, buildRuntimeCrmSeoMetadata(route.locale));
      return;
    }

    if (route.kind === "fortnox_public") {
      applySeoMetadata(document, buildRuntimeFortnoxPublicSeoMetadata(route.page));
      return;
    }

    if (route.kind === "quickbooks_legal") {
      return;
    }

    if (route.kind === "quickbooks_public" || route.kind === "quickbooks_action" || (route.kind === "account" && route.accountPanel === "quickbooks")) {
      applySeoMetadata(document, buildRuntimeQuickBooksSeoMetadata(route.path, route.kind === "quickbooks_public"));
      return;
    }

    if (route.kind === "us_services") {
      applySeoMetadata(document, dbk_buildRuntimeUsServicesMetadata(window.location.origin));
      return;
    }
    if (route.kind === "us_landing" || route.kind === "market_intake" || route.kind === "us_market_onboarding") {
      const metadata = buildRuntimeSeoMetadata("en", "home");
      const usHomepage = route.kind === "us_landing" ? dbkBuildUsHomepageMetadata(window.location.origin) : null;
      metadata.title = usHomepage?.title ?? dbkUsPublicCopy.title; metadata.description = usHomepage?.description ?? dbkUsPublicCopy.description; metadata.keywords = usHomepage?.keywords ?? "US bookkeeping request, bookkeeping scope, DynamicBooks";
      metadata.htmlLang = "en-US"; metadata.robots = route.kind === "us_landing" ? metadata.robots : "noindex,nofollow";
      metadata.og.title = metadata.title; metadata.og.description = metadata.description;
      metadata.twitter.title = metadata.title; metadata.twitter.description = metadata.description;
      const marketPath = route.kind === "us_landing" ? "/" : route.kind === "market_intake" ? "/us/intake" : "/us/proposal-onboarding";
      metadata.canonicalUrl = new URL(marketPath, window.location.origin).toString(); metadata.og.url = metadata.canonicalUrl;
      if (usHomepage) {
        metadata.og.image = usHomepage.imageUrl; metadata.og.imageAlt = usHomepage.imageAlt; metadata.og.imageWidth = usHomepage.imageWidth; metadata.og.imageHeight = usHomepage.imageHeight;
        metadata.twitter.image = usHomepage.imageUrl; metadata.twitter.imageAlt = usHomepage.imageAlt;
        metadata.jsonLd = usHomepage.jsonLd;
      } else {
        metadata.jsonLd = { "@context": "https://schema.org", "@graph": [{ "@type": "WebPage", name: metadata.title, description: metadata.description, url: metadata.canonicalUrl, inLanguage: "en-US" }] };
      }
      applySeoMetadata(document, metadata);
      return;
    }
    const ratingsTarget = getDynamicBooksRatingsTarget(route.locale);
    const ratingsBootstrap = getDynamicBooksPublicRatingsBootstrap(ratingsTarget);
    applySeoMetadata(document, buildRuntimeSeoMetadata(route.locale, seoKey, {
      publicRatingsSummary: ratingsBootstrap?.summary ?? null
    }));
  }, [locationSnapshot, route.kind, route.locale, articleKey, fortnoxPublicPageKey, seoKey]);

  useEffect(() => {
    if (route.kind !== "article" || !route.shouldRedirectToCanonical || typeof window === "undefined") {
      return;
    }

    if (window.location.href !== route.canonicalUrl) {
      window.location.replace(route.canonicalUrl);
    }
  }, [route]);

  useEffect(() => {
    const targetId = getHashTargetId();
    if (!targetId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: "start" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [locationSnapshot]);

  const pathname = getCurrentPathname();
  if (route.kind === "us_landing") return <UnitedStatesLandingPage />;
  if (route.kind === "us_services") return <UnitedStatesServicesCatalog />;
  if (route.kind === "market_intake") return <MarketIntakePage />;
  if (route.kind === "us_market_onboarding") return <UsMarketOnboardingPage />;
  if (route.kind === "account" && route.accountPanel === "quickbooks") {
    return renderAsyncRoute(<AccountSession locale={route.locale} panel="quickbooks" />, route.locale);
  }

  if (isFortnoxActivationUnavailableRoute(pathname)) {
    return renderAsyncRoute(<FortnoxActivationUnavailablePage locale={route.locale} />, route.locale);
  }

  if (isAccountRoute(pathname)) {
    return renderAsyncRoute(<AccountSession locale={route.locale} />, route.locale);
  }

  if (route.kind === "quickbooks_public") {
    return renderAsyncRoute(<QuickBooksPublicPage />, route.locale);
  }

  if (route.kind === "quickbooks_action") {
    return renderAsyncRoute(<QuickBooksActionPage action={route.action} />, route.locale);
  }


  if (route.kind === "quickbooks_legal") {
    return renderAsyncRoute(<QuickBooksLegalPage kind={route.documentKind} />, route.locale);
  }

  if (route.kind === "crm") {
    return renderAsyncRoute(<CrmRouteShell route={route} />, route.locale);
  }

  if (route.kind === "proposal_onboarding") {
    return renderAsyncRoute(<CustomerOnboardingPage route={route} />, route.locale);
  }

  if (route.kind === "article") {
    return renderAsyncRoute(<ArticlePage route={route} />, route.locale);
  }

  if (route.kind === "fortnox_public") {
    return renderAsyncRoute(<FortnoxPublicPageLayout page={route.page} />, route.locale);
  }

  if (route.kind === "not_found") {
    return renderAsyncRoute(<NotFoundPage route={route} />, route.locale);
  }

  return isCaseRoute(pathname)
    ? renderAsyncRoute(<CaseStatusPage route={route} />, route.locale)
    : <LandingPage route={route} />;
}

function renderAsyncRoute(content: ReactNode, locale: "sv" | "en") {
  const loadingLabel = locale === "sv" ? "Laddar sidan…" : "Loading page…";
  return (
    <Suspense
      fallback={
        <main className="route-loading" id="main-content" aria-busy="true">
          <p role="status">{loadingLabel}</p>
        </main>
      }
    >
      {content}
    </Suspense>
  );
}

function useBrowserLocationSnapshot(): string {
  const [locationSnapshot, setLocationSnapshot] = useState(() => getLocationSnapshot());

  useEffect(() => {
    const handleLocationChange = () => setLocationSnapshot(getLocationSnapshot());

    window.addEventListener("hashchange", handleLocationChange);
    window.addEventListener("popstate", handleLocationChange);

    return () => {
      window.removeEventListener("hashchange", handleLocationChange);
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  return locationSnapshot;
}

function getLocationSnapshot(): string {
  if (typeof window === "undefined") {
    return "/";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getCurrentPathname(): string {
  if (typeof window === "undefined") {
    return "/";
  }

  return window.location.pathname;
}

function getHashTargetId(): string | null {
  if (typeof window === "undefined" || !window.location.hash) {
    return null;
  }

  const rawTarget = window.location.hash.replace(/^#/, "");
  if (!rawTarget) {
    return null;
  }

  try {
    return decodeURIComponent(rawTarget);
  } catch {
    return rawTarget;
  }
}

function isCaseRoute(pathname: string): boolean {
  return pathname === "/case" || pathname === "/en/case" || /^\/(?:en\/)?case\/[^/]+$/.test(pathname);
}

function isAccountRoute(pathname: string): boolean {
  return pathname === "/account"
    || pathname === "/en/account"
    || pathname === "/account/integrationer/fortnox"
    || pathname === "/en/account/integrations/fortnox";
}

function isFortnoxActivationUnavailableRoute(pathname: string): boolean {
  return pathname === "/account/integrationer/fortnox"
    || pathname === "/en/account/integrations/fortnox";
}
